import { useCallback, useContext, useMemo, useState } from "react";
import cx from "classnames";
import type { OperativeDaysModel, ProjectTaskHierarchyModel } from "api/model";
import { Loader } from "core/components";
import { ProjectHierarchyProjectTasks } from "./ProjectHierarchyProjectTasks";
import { TasksContext } from "core/context";

export interface ProjectHierarchyProps {
  projectId: number;
  gangId: number;

  className?: string;
  depth: number;
  isOverviewPanelOpen?: boolean;

  data: ProjectTaskHierarchyModel;
  isLoading?: boolean;

  expandedTasks: Set<number>;
  onToggleTask: (id: number) => void;

  forceShowProjectTasks?: boolean;
  forceExpandHierarchies?: boolean;

  isSavingProjectTaskId?: number;
  handleIncludeProjectTaskInTaskList?: (
    projectTaskId: number,
    include: boolean
  ) => void;

  hasSelectedOperatives?: boolean;
  isManageMode?: boolean;

  handleExpandTask?: (id: number | null) => void;
  calendarData?: OperativeDaysModel;
  onRemoveProjectTaskAssignment?: (projectTaskAssignmentId: number) => void;
}

function hierarchyHasVisibleTasks(
  hierarchy: ProjectTaskHierarchyModel,
  isManageMode: boolean | undefined,
  inspectionMode: boolean | undefined,
  selectedTaskIds: Set<number>
): boolean {
  const tasks = isManageMode
    ? hierarchy.ProjectTasks
    : inspectionMode
    ? hierarchy.ProjectTasks?.filter(
        (t) => t.IsIncludedInTaskList || selectedTaskIds.has(t.Id!)
      )
    : hierarchy.ProjectTasks?.filter((t) => t.IsIncludedInTaskList);

  if (tasks?.length) return true;

  return (
    hierarchy.ChildHierarchies?.some((child) =>
      hierarchyHasVisibleTasks(child, isManageMode, inspectionMode, selectedTaskIds)
    ) ?? false
  );
}

export const ProjectHierarchy: React.FC<ProjectHierarchyProps> = (props) => {
  const {
    projectId,
    gangId,
    className,
    depth,
    isOverviewPanelOpen,
    isLoading,
    data,
    expandedTasks,
    hasSelectedOperatives,
    isManageMode,
    onToggleTask,
    forceShowProjectTasks,
    forceExpandHierarchies,
    isSavingProjectTaskId,
    handleIncludeProjectTaskInTaskList,
    handleExpandTask,
    calendarData,
    onRemoveProjectTaskAssignment,
  } = props;

  const isSafari = false;

  const [showProjectTasks, setShowProjectTasks] = useState(false);
  const [hideForcedProjectTasks, setHideForcedProjectTasks] = useState(false);
  const [selectedProjectTask, setSelectedProjectTask] = useState(
    new Set<number>()
  );
  const taskCtx = useContext(TasksContext);

  const inspectionMode =
    taskCtx?.inspectionMode?.general || taskCtx?.inspectionMode?.byTask;

  const selectedTaskIds = useMemo(() => {
    if (!inspectionMode || !taskCtx?.selectedDetailedCells?.length)
      return new Set<number>();
    const assignmentIds = taskCtx.selectedDetailedCells.flatMap(
      (cell) => cell.Assignments?.map((a) => a.ProjectTaskAssignmentId) ?? []
    );
    const taskIds = assignmentIds
      .map(
        (assignmentId) =>
          calendarData?.TaskAssignments?.find((ta) => ta.Id === assignmentId)
            ?.TaskId
      )
      .filter((id): id is number => id !== undefined);
    return new Set(taskIds);
  }, [inspectionMode, taskCtx?.selectedDetailedCells, calendarData?.TaskAssignments]);

  const visibleProjectTasks = isManageMode
    ? data.ProjectTasks
    : inspectionMode
    ? data.ProjectTasks?.filter(
        (t) => t.IsIncludedInTaskList || selectedTaskIds.has(t.Id!)
      )
    : data.ProjectTasks?.filter((t) => t.IsIncludedInTaskList);
  const hasProjectTasks = Boolean(visibleProjectTasks?.length);

  const visibleChildHierarchies = data.ChildHierarchies?.filter((child) =>
    hierarchyHasVisibleTasks(child, isManageMode, inspectionMode, selectedTaskIds)
  );
  const hasChildHierarchies = Boolean(visibleChildHierarchies?.length);

  const handleProjectTaskToggle = useCallback(
    (id: number) => {
      if (!isManageMode && !hasSelectedOperatives) {
        const taskName = data.ProjectTasks?.find(
          (projectTask) => projectTask.Id === id
        )?.Name;

        if (taskName) {
          taskCtx?.updateTaskName(taskName);
        }

        if (!selectedProjectTask.has(id)) {
          handleExpandTask?.(id);
          taskCtx?.updateInspectionMode({
            general: true,
            byTask: true,
          });
        } else {
          handleExpandTask?.(null);
        }
      }
      setSelectedProjectTask((oldSet) => {
        const newSet = new Set(oldSet);
        if (!hasSelectedOperatives) {
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
        }
        return newSet;
      });
    },
    [
      selectedProjectTask,
      hasSelectedOperatives,
      isManageMode,
      data.ProjectTasks,
      handleExpandTask,
      taskCtx,
    ]
  );

  const isExpanded = expandedTasks.has(data.Id!) || forceExpandHierarchies;
  const areProjectTasksVisible =
    ((forceShowProjectTasks && !hideForcedProjectTasks) || showProjectTasks) &&
    hasProjectTasks;

  const handleVisibilityClick = useCallback(() => {
    if (hasProjectTasks) {
      if (forceShowProjectTasks && !hasChildHierarchies) {
        setHideForcedProjectTasks((hidden) => !hidden);
        return;
      }

      setShowProjectTasks((show) => !show);
      // OP2-71 Display Child Hierarchies if they exist
      if (hasChildHierarchies) {
        onToggleTask?.(data.Id!);
      }
    } else {
      onToggleTask?.(data.Id!);
    }
    // OP2-71 Added data.ChildHierarchies?.length
  }, [
    data.Id,
    forceShowProjectTasks,
    hasProjectTasks,
    hasChildHierarchies,
    onToggleTask,
  ]);

  if (!hasProjectTasks && !hasChildHierarchies) return null;

  return (
    <div
      className={cx("project-hierarchy hierarchy-with-children", className, {
        "with-fixed-header": false, //!isSearchMode(),
        "padding-bottom-1x":
          isExpanded || areProjectTasksVisible, // includePaddingBottom(),
        "hierarchy-with-children--depth-even": depth % 2 === 0,
        "hierarchy-with-children--depth-odd": depth % 2 !== 0,
      })}
    >
      <div
        className={cx("project-task-hierarchy", {
          "full-width": false, // !taskVisibilityButtonVisible(),
          ["depth-" + depth]: true,
          "has-even-depth-parent": depth % 2 === 0,
          "reset-sticky": isSafari,
        })}
        title={data.Name}
      >
        {data.Name}
      </div>
      <button
        className={cx("task-visibility", {
          "unassigned-visible": isExpanded,
          ["depth-" + depth]: true,
          "reset-sticky": isSafari,
        })}
        title={
          isExpanded ? "Collapse hierarchy level" : "Expand hierarchy level"
        }
        onClick={handleVisibilityClick}
      />
      {areProjectTasksVisible &&
        visibleProjectTasks?.map((projectTask, idx, arr) => (
          <ProjectHierarchyProjectTasks
            key={projectTask.Id}
            projectId={projectId}
            gangId={gangId}
            isFirst={idx === 0}
            isLast={idx === arr.length - 1}
            depth={depth}
            task={projectTask}
            selectedProjectTasks={selectedProjectTask}
            handleProjectTaskClick={() =>
              handleProjectTaskToggle(projectTask.Id!)
            }
            isSavingProjectTaskId={isSavingProjectTaskId}
            handleIncludeProjectTaskInTaskList={
              handleIncludeProjectTaskInTaskList
            }
            hasSelectedOperatives={hasSelectedOperatives}
            isManageMode={isManageMode}
            calendarData={calendarData}
            onRemoveProjectTaskAssignment={onRemoveProjectTaskAssignment}
          />
        ))}
      {/* <!-- ko if: $component.isOverviewPanelOpen --> */}
      {/* <project-task-assignments params="assignments: projectTaskAssignments"></project-task-assignments> */}
      {/* <!-- /ko --> */}
      {isLoading && (
        <div className="task-details full-width">
          <Loader className="pinned" />
        </div>
      )}
      {isExpanded &&
        visibleChildHierarchies?.map((hierarchy, idx) => (
          <ProjectHierarchy
            key={hierarchy.Id}
            projectId={projectId}
            gangId={gangId}
            className={cx({
              "no-margin-top": idx === 0 && true,
            })}
            data={hierarchy}
            depth={depth + 1}
            isOverviewPanelOpen={isOverviewPanelOpen}
            expandedTasks={expandedTasks}
            onToggleTask={onToggleTask}
            forceShowProjectTasks={forceShowProjectTasks}
            forceExpandHierarchies={forceExpandHierarchies}
            isSavingProjectTaskId={isSavingProjectTaskId}
            hasSelectedOperatives={hasSelectedOperatives}
            isManageMode={isManageMode}
            handleIncludeProjectTaskInTaskList={
              handleIncludeProjectTaskInTaskList
            }
            handleExpandTask={handleExpandTask}
            calendarData={calendarData}
            onRemoveProjectTaskAssignment={onRemoveProjectTaskAssignment}
          />
        ))}
    </div>
  );
};
